import React, { useState } from 'react';
import { Box, Chip, Collapse, FormControlLabel, IconButton, List, ListItemButton, Stack, Switch, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { MagicReportRevisionList, MagicReportRevisionSession, MagicReportRevisionSummary } from '../magic-report.type';
import { DrawNames, dayLabel, labelForKind, labelForStep, labelsForKeys, parseUtc, timeLabel } from './version-labels';

/**
 * What is selected: a whole session (its latest version, compared with the version before the
 * session), or one version on its own (compared with the version just before it).
 */
export interface VersionSelection {
  revisionId: string;
  wholeSession: boolean;
}

interface VersionListProps {
  versions?: MagicReportRevisionList;
  isLoading: boolean;
  selection?: VersionSelection;
  onSelect: (selection: VersionSelection) => void;
  namedOnly: boolean;
  onNamedOnlyChange: (namedOnly: boolean) => void;
  drawNames?: DrawNames;
}

const changeSummary = (revision: { step?: string | null; changedKeys: string[] }, drawNames?: DrawNames) => {
  const labels = labelsForKeys(revision.changedKeys, drawNames);
  const changes = labels.length > 3 ? `${labels.slice(0, 3).join(', ')} +${labels.length - 3} more` : labels.join(', ');
  return [labelForStep(revision.step), changes].filter(Boolean).join(' · ');
};

const RevisionBadges: React.FC<{ revision: MagicReportRevisionSummary; isCurrent: boolean }> = ({ revision, isCurrent }) => {
  const kindLabel = labelForKind(revision.kind);
  if (!isCurrent && !kindLabel) return null;
  return (
    <Stack direction='row' spacing={0.5} sx={{ mt: 0.5 }}>
      {isCurrent && <Chip label='Current version' size='small' color='primary' variant='outlined' />}
      {kindLabel && <Chip label={kindLabel} size='small' variant='outlined' />}
    </Stack>
  );
};

/** One revision row: time (or name), author, step and what changed. */
const RevisionRow: React.FC<{
  revision: MagicReportRevisionSummary;
  isCurrent: boolean;
  selected: boolean;
  indent?: boolean;
  onSelect: (revisionId: string) => void;
  action?: React.ReactNode;
  drawNames?: DrawNames;
}> = ({ revision, isCurrent, selected, indent, onSelect, action, drawNames }) => {
  const time = parseUtc(revision.lastAmendedOn);
  const summary = changeSummary(revision, drawNames);
  return (
    <ListItemButton
      selected={selected}
      onClick={() => onSelect(revision.id)}
      sx={{ alignItems: 'flex-start', pl: indent ? 4 : 2, pr: 1, borderRadius: 1 }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant='body2' sx={{ fontWeight: 600 }}>
          {revision.name || (time ? timeLabel(time) : '')}
        </Typography>
        {revision.name && time && (
          <Typography variant='caption' color='text.secondary' component='div'>
            {timeLabel(time)}
          </Typography>
        )}
        {revision.author?.name && (
          <Typography variant='caption' color='text.secondary' component='div'>
            {revision.author.name}
          </Typography>
        )}
        {summary && (
          <Typography variant='caption' color='text.secondary' component='div' sx={{ wordBreak: 'break-word' }}>
            {summary}
          </Typography>
        )}
        <RevisionBadges revision={revision} isCurrent={isCurrent} />
      </Box>
      {action}
    </ListItemButton>
  );
};

/**
 * One editing session. Selecting it shows everything the session changed; expanding it lists each
 * version in it (newest first), which can be selected on its own.
 */
const SessionRow: React.FC<{
  session: MagicReportRevisionSession;
  currentRevisionId?: string | null;
  selection?: VersionSelection;
  onSelect: (selection: VersionSelection) => void;
  drawNames?: DrawNames;
}> = ({ session, currentRevisionId, selection, onSelect, drawNames }) => {
  const [latest] = session.revisions;
  const hasSeveral = session.revisions.length > 1;
  const singleSelected = hasSeveral && !selection?.wholeSession && session.revisions.some(r => r.id === selection?.revisionId);
  const [expanded, setExpanded] = useState(singleSelected);

  return (
    <>
      <RevisionRow
        // The session row shows the session's latest version with everything the session changed.
        revision={hasSeveral ? { ...latest, changedKeys: session.changedKeys } : latest}
        isCurrent={latest.id === currentRevisionId}
        selected={selection?.revisionId === latest.id && (selection.wholeSession || !hasSeveral)}
        onSelect={revisionId => onSelect({ revisionId, wholeSession: hasSeveral })}
        drawNames={drawNames}
        action={
          hasSeveral ? (
            <IconButton
              size='small'
              aria-label={expanded ? 'Hide the versions in this session' : `Show the ${session.revisions.length} versions in this session`}
              onClick={event => {
                event.stopPropagation();
                setExpanded(prev => !prev);
              }}
            >
              {expanded ? <ExpandMoreIcon fontSize='small' /> : <ChevronRightIcon fontSize='small' />}
            </IconButton>
          ) : undefined
        }
      />
      {hasSeveral && (
        <Collapse in={expanded || singleSelected} unmountOnExit>
          {session.revisions.map(revision => (
            <RevisionRow
              key={revision.id}
              revision={revision}
              isCurrent={false}
              selected={revision.id === selection?.revisionId && !selection.wholeSession}
              indent
              onSelect={revisionId => onSelect({ revisionId, wholeSession: false })}
              drawNames={drawNames}
            />
          ))}
        </Collapse>
      )}
    </>
  );
};

/** The right-hand list of versions, grouped under Today / Yesterday / dates like Google Docs. */
const VersionList: React.FC<VersionListProps> = ({ versions, isLoading, selection, onSelect, namedOnly, onNamedOnlyChange, drawNames }) => {
  const sessions = versions?.sessions ?? [];
  const groups: { day: string; sessions: MagicReportRevisionSession[] }[] = [];
  sessions.forEach(session => {
    const ended = parseUtc(session.endedOn);
    const day = ended ? dayLabel(ended) : '';
    const group = groups[groups.length - 1];
    if (group?.day === day) group.sessions.push(session);
    else groups.push({ day, sessions: [session] });
  });

  return (
    <Box sx={{ width: 340, borderLeft: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Typography variant='h6'>Version history</Typography>
        <FormControlLabel
          control={<Switch size='small' checked={namedOnly} onChange={event => onNamedOnlyChange(event.target.checked)} />}
          label={<Typography variant='body2'>Only show named versions</Typography>}
        />
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto', px: 1, pb: 2 }}>
        {isLoading ? (
          <Typography variant='body2' color='text.secondary' sx={{ px: 1 }}>
            Loading versions…
          </Typography>
        ) : sessions.length === 0 ? (
          <Typography variant='body2' color='text.secondary' sx={{ px: 1 }}>
            {namedOnly ? 'No named versions yet.' : 'No versions yet. Versions are recorded automatically as the report is saved.'}
          </Typography>
        ) : (
          groups.map(group => (
            <Box key={group.day} sx={{ mb: 1 }}>
              <Typography variant='overline' color='text.secondary' sx={{ px: 1 }}>
                {group.day}
              </Typography>
              <List dense disablePadding>
                {group.sessions.map(session => (
                  <SessionRow
                    key={session.latestRevisionId}
                    session={session}
                    currentRevisionId={versions?.currentRevisionId}
                    selection={selection}
                    onSelect={onSelect}
                    drawNames={drawNames}
                  />
                ))}
              </List>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

export default VersionList;
